// Build-time mode flag — distinguishes Agoracle Arena (Game module) from
// the Live trading platform.
//
// Set via the `REACT_APP_MODE` environment variable per DECISIONS.md
// 2026-05-02 ("Game module is a separate architectural instance from
// Live platform"). Two valid values:
//   - 'game' → Agoracle Arena (Supabase project ref revhjqwhglqxmogpkcui).
//             Visual register SHOULD render the amber overlay (badge,
//             PLAY CREDITS label, amber cockpit border, footer disclaimer)
//             per CRYPTO_ARCHITECTURE.md §6.3 (spec pending — see
//             SESSION_HANDOFF 2026-05-03 finding (b)).
//   - undefined / anything else → Live platform (project ref
//             zdtfjlkkjosjrjzffosd). Default behaviour, no overlay.
//
// CRA inlines `process.env.REACT_APP_*` at build time; the comparison
// runs once at module load and the result is a stable boolean.
//
// Visual-register components import the boolean (not the raw env var)
// so that:
//   1. Future flag-source changes (e.g. Vercel preview-vs-prod env
//      injection) only touch this one file.
//   2. The amber-vs-green decision is a single source of truth — every
//      conditional in the UI reads `IS_GAME_MODE`, never re-reads
//      `process.env`.
//   3. Tests can mock this module rather than the entire `process.env`
//      surface.

export const IS_GAME_MODE: boolean =
  (process.env.REACT_APP_MODE ?? '').toLowerCase() === 'game';

// Convenience labels — colocated here so the visual-register components
// don't each invent their own copy. Architect-confirmed wording
// (2026-05-03):
//   • Hazard Stripe badge: ⬡ AGORACLE ARENA
//   • Balance: PLAY CREDITS alongside all OC amounts (so OC stays as
//     the unit; PLAY CREDITS is appended in game mode)
//   • Footer disclaimer: every page, sticky bottom
export const MODE_LABEL = IS_GAME_MODE ? 'AGORACLE ARENA' : 'AGORACLE';
export const BALANCE_UNIT_LABEL = IS_GAME_MODE ? 'OC · PLAY CREDITS' : 'OC';
export const ARENA_FOOTER_TEXT = 'This is a simulated environment. No real money involved.';
export const ARENA_AMBER = '#F59E0B'; // Tailwind amber-500

// ── Stripped-down Arena flag ────────────────────────────────────────────
//
// Per DECISIONS.md 2026-05-13 (I-046 promoted), the Arena's user-facing
// surface is stripped to a minimum viable parimutuel market. Held-back
// features stay in code but are not surfaced to users. Each held-back
// feature graduates from "held in reserve" to "shipped publicly" only
// when (a) user-side signal suggests value, (b) the underlying mechanism
// is verified operationally stable, (c) the validation infrastructure
// has capacity, (d) a Substack post draft exists explaining the change.
//
// This single flag gates every held-back UI affordance — Mint Cockpit
// CTA, Penumbra Chart components, Discovery Bonus surface, Sovereign
// Veto endpoints, Citizen Jury delegation, citizenship-tier exposure,
// social-forking affordances, and ceremonial vocabulary in operational
// UI. Defaults TRUE in the Arena context (game mode); FALSE on Live
// so the full feature set ships when Live launches.
//
// When held-back features roll out per I-046's evidence-based pattern,
// individual feature flags can be added at that point (one per feature
// being surfaced). For now a single boolean is the right shape — there
// is no held-back feature being conditionally surfaced in this phase.
//
// Components import IS_STRIPPED_DOWN and conditionally render — they do
// NOT delete code. Held back means "not surfaced in the UI", per
// DECISIONS.md 2026-05-13 I-046 / Session A findings §3.3.
//
// ── Relationship to IS_GAME_MODE and REACT_APP_MODE ───────────────────
//
// Three names exist for what is, today, one value:
//
//   REACT_APP_MODE='game'  — the env-var source (build-time inlining)
//   IS_GAME_MODE           — the visual-register flag (Arena badge,
//                            amber overlay, PLAY CREDITS label,
//                            ArenaFooter disclaimer)
//   IS_STRIPPED_DOWN       — the I-046 held-back-feature gate
//
// **Same VALUE, distinct SEMANTIC purpose.** IS_GAME_MODE asks "are we
// rendering the Arena visual register?"; IS_STRIPPED_DOWN asks "are
// I-046 held-back features hidden?". Both currently resolve from the
// same `REACT_APP_MODE === 'game'` predicate because the Arena phase
// IS the stripped-down phase. They are aliased here, not unified, so
// the two semantic dimensions can diverge later without a code
// rewrite — see the divergence path below.
//
// ── Divergence path (forward-looking) ─────────────────────────────────
//
// When held-back features begin rolling out per I-046, the cleanest
// shape is **per-feature flags** (e.g. FEATURE_PENUMBRA,
// FEATURE_DISCOVERY_BONUS) toggled individually as user-side signal
// justifies each surface. At that point, IS_STRIPPED_DOWN's role
// changes from "all-or-nothing held back" to "default-true unless any
// per-feature flag flips on", or it may retire entirely in favour of
// per-feature gates. IS_GAME_MODE persists independently as the
// visual-register flag (Arena badge stays as long as Arena exists,
// regardless of which features are held back).
//
// A possible interim shape (not yet implemented): introduce a
// FEATURES_SURFACED set keyed on feature names, with IS_STRIPPED_DOWN
// derived as `FEATURES_SURFACED.size === 0`. Per-feature surfaces
// then check `FEATURES_SURFACED.has('penumbra')`. This is the
// concretisation that the IDEAS-entry followup proposed in
// SESSION_HANDOFF.md should explore.
//
// ── Naming-drift audit (Architect direction 2026-05-13) ───────────────
//
// The terminology drift between the inventory document
// (IS_STRIPPED_DOWN) and the existing visual-register flag
// (IS_GAME_MODE) and the env var (REACT_APP_MODE='game') was flagged
// during gate-22 verification as worth consolidating. After review
// the conclusion was: keep both names, clarify the semantic boundary
// (this comment block), and document the divergence path. A single-
// name consolidation would touch ~75 sites for marginal clarity gain
// and would obscure the visual-register-vs-feature-gating distinction
// that may matter when per-feature flags arrive.
export const IS_STRIPPED_DOWN: boolean = IS_GAME_MODE;
